import React from "react";
import { observer } from "mobx-react-lite";
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Paper,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    Divider,
} from "@mui/material";
import { DateTime } from "luxon";
import { Department, EcommerceEventList } from "../../models/Event";
import CartStore from "../../stores/CartStore";
import toast from "../../utils/toast";
import { getErrorMessage } from "../../utils/helpers";
import {
    CNE_GA_EVENT_ID,
    CNE_GA_FILTER_DEPARTMENT_ID,
    CNE_GA_TARGET_DEPARTMENT_ID,
} from "../../constants/gaTickets";

type Props = {
    selectedTournaments: Department[];
    eventList: EcommerceEventList;
    cartStore: CartStore | null;
    onComplete: () => void;
    onBack: () => void;
};

type GaRequirement = {
    date: string;
    dateFormatted: string;
    tournamentCount: number;
    existingGaTickets: number;
    neededGaTickets: number;
    gaTicketsToAdd: number;
    gaDepartments: Department[];
};

const GaTicketPrompt = observer(
    ({
        selectedTournaments,
        eventList,
        cartStore,
        onComplete,
        onBack,
    }: Props) => {
        const [loading, setLoading] = React.useState(false);

        // Group tournaments by event date and calculate GA requirements
        const gaRequirements = React.useMemo(() => {
            if (!cartStore?.cart) return [];

            // Group tournaments by date
            const tournamentsByDate = selectedTournaments.reduce(
                (acc, tournament) => {
                    const date = DateTime.fromMillis(tournament.to).toFormat(
                        "yyyy-MM-dd",
                    );
                    const dateFormatted = DateTime.fromMillis(
                        tournament.to,
                    ).toFormat("MMMM d, yyyy");

                    if (!acc[date]) {
                        acc[date] = {
                            date,
                            dateFormatted,
                            tournaments: [],
                        };
                    }
                    acc[date].tournaments.push(tournament);
                    return acc;
                },
                {} as Record<
                    string,
                    {
                        date: string;
                        dateFormatted: string;
                        tournaments: Department[];
                    }
                >,
            );

            // Count total GA tickets in cart (they work for any day)
            const totalGaTicketsInCart =
                cartStore.cart?.items.filter(
                    (item) =>
                        item.department.id === CNE_GA_FILTER_DEPARTMENT_ID &&
                        item.event.id === CNE_GA_EVENT_ID,
                ).length ?? 0;

            const gaEvent = eventList.events.find(
                (event) => event.id === CNE_GA_EVENT_ID,
            );
            const gaDepartments =
                gaEvent?.departments.filter(
                    (dept) => dept.id === CNE_GA_TARGET_DEPARTMENT_ID,
                ) || [];

            // Calculate GA requirements for each date
            const dateEntries = Object.values(tournamentsByDate);
            const totalUniqueDates = dateEntries.length;
            const totalGaTicketsNeeded = totalUniqueDates; // 1 per unique date
            const totalGaTicketsToAdd = Math.max(
                0,
                totalGaTicketsNeeded - totalGaTicketsInCart,
            );

            // Distribute GA tickets across tournament days (1 per day)
            const results = dateEntries
                .map(({ date, dateFormatted, tournaments }, index) => {
                    // Count total registrations for this date (for display purposes)
                    const tournamentCount = tournaments.length;

                    // Each date gets 1 GA ticket if available
                    const hasGaTicket = index < totalGaTicketsInCart;
                    const existingGaTickets = hasGaTicket ? 1 : 0;
                    const neededGaTickets = 1;
                    const gaTicketsToAdd = hasGaTicket ? 0 : 1;

                    return {
                        date,
                        dateFormatted,
                        tournamentCount,
                        existingGaTickets,
                        neededGaTickets,
                        gaTicketsToAdd,
                        gaDepartments,
                        isFirstRow: index === 0,
                        isTournamentDay: true,
                    };
                })
                .sort((a, b) => a.date.localeCompare(b.date));

            // If we have extra GA tickets, show them as a separate row
            const extraGaTickets = totalGaTicketsInCart - totalUniqueDates;
            if (extraGaTickets > 0) {
                results.push({
                    date: "extra",
                    dateFormatted: "Extra GA Tickets",
                    tournamentCount: 0,
                    existingGaTickets: extraGaTickets,
                    neededGaTickets: 0,
                    gaTicketsToAdd: 0,
                    gaDepartments,
                    isFirstRow: false,
                    isTournamentDay: false,
                });
            }

            return results;
        }, [selectedTournaments, eventList, cartStore?.cart]);

        const totalGaToAdd = gaRequirements.reduce(
            (sum, req) => sum + req.gaTicketsToAdd,
            0,
        );
        const hasGaRequirements = gaRequirements.some(
            (req) => req.gaTicketsToAdd > 0,
        );

        const handleAddGaTickets = async () => {
            if (!cartStore || !hasGaRequirements) {
                onComplete();
                return;
            }

            setLoading(true);

            try {
                // Use the first available GA department for this date
                const gaDepartment = gaRequirements[0].gaDepartments[0];

                // Add exactly 1 GA ticket per date (gaTicketsToAdd should be 1 or 0)
                await cartStore.setGaQuantity(
                    gaDepartment.id,
                    false,
                    gaRequirements.length, // Always add exactly 1 ticket per date
                    gaDepartment,
                    undefined,
                    false,
                );

                toast.success(
                    `Added ${totalGaToAdd} GA ticket${
                        totalGaToAdd > 1 ? "s" : ""
                    } to cart (1 per day).`,
                );
                onComplete();
            } catch (error) {
                console.error("Error adding GA tickets:", error);
                toast.error(getErrorMessage(error));
            } finally {
                setLoading(false);
            }
        };

        const handleSkipGaTickets = () => {
            onComplete();
        };

        return (
            <Box>
                <Paper sx={{ p: 3, mb: 4 }}>
                    <Typography variant="h4" gutterBottom textAlign="center">
                        General Admission Tickets
                    </Typography>
                    <Typography
                        variant="body1"
                        textAlign="center"
                        color="text.secondary"
                    >
                        Tournament participants need one general admission
                        ticket per day for event access.
                    </Typography>
                </Paper>

                <Card sx={{ mb: 4 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            GA Ticket Requirements
                        </Typography>

                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Event Date</TableCell>
                                        <TableCell align="center">
                                            Tournament Registrations
                                        </TableCell>
                                        <TableCell align="center">
                                            GA Tickets Needed (Per Day)
                                        </TableCell>
                                        <TableCell align="center">
                                            GA Tickets in Cart
                                        </TableCell>
                                        <TableCell align="center">
                                            GA Tickets to Add
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {gaRequirements.map((requirement) => (
                                        <TableRow key={requirement.date}>
                                            <TableCell>
                                                <Typography
                                                    variant="body2"
                                                    fontWeight="medium"
                                                    sx={{
                                                        color: !requirement.isTournamentDay
                                                            ? "text.secondary"
                                                            : "inherit",
                                                    }}
                                                >
                                                    {requirement.dateFormatted}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                {requirement.isTournamentDay ? (
                                                    <Chip
                                                        label={
                                                            requirement.tournamentCount
                                                        }
                                                        color="primary"
                                                        size="small"
                                                    />
                                                ) : (
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                    >
                                                        —
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={
                                                        requirement.neededGaTickets
                                                    }
                                                    color="info"
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={
                                                        requirement.existingGaTickets
                                                    }
                                                    color={
                                                        requirement.existingGaTickets >=
                                                        requirement.neededGaTickets
                                                            ? "success"
                                                            : "default"
                                                    }
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={
                                                        requirement.gaTicketsToAdd
                                                    }
                                                    color={
                                                        requirement.gaTicketsToAdd >
                                                        0
                                                            ? "warning"
                                                            : "success"
                                                    }
                                                    size="small"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {gaRequirements.some(
                            (req) => req.gaDepartments.length === 0,
                        ) && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                Some dates don&apos;t have GA tickets available.
                                Please contact support if you need assistance.
                            </Alert>
                        )}
                    </CardContent>
                </Card>

                {hasGaRequirements && (
                    <Paper sx={{ p: 3, mb: 4, bgcolor: "info.light" }}>
                        <Typography
                            variant="h6"
                            gutterBottom
                            color="info.contrastText"
                        >
                            GA Tickets Recommended
                        </Typography>
                        <Typography
                            variant="body1"
                            color="info.contrastText"
                            sx={{ mb: 2 }}
                        >
                            We recommend adding {totalGaToAdd} additional GA
                            ticket
                            {totalGaToAdd > 1 ? "s" : ""} to access the CNE
                            grounds if you have not already purchased them.
                        </Typography>
                        <Typography
                            variant="body2"
                            color="info.contrastText"
                            sx={{ mb: 2 }}
                        >
                            You only need one general admission ticket per day,
                            regardless of how many tournaments you&apos;re
                            registered for. GA tickets can be used on any day
                            you attend.
                        </Typography>
                        <Typography
                            variant="body2"
                            color="info.contrastText"
                            sx={{ fontStyle: "italic" }}
                        >
                            You can skip this step if you&apos;ve already
                            purchased GA tickets in a separate order.
                        </Typography>
                    </Paper>
                )}

                {!hasGaRequirements && (
                    <Alert severity="success" sx={{ mb: 4 }}>
                        <Typography variant="body1">
                            Great! You already have all the required GA tickets
                            in your cart.
                        </Typography>
                    </Alert>
                )}

                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 2,
                        flexWrap: "wrap",
                    }}
                >
                    <Button
                        variant="outlined"
                        onClick={onBack}
                        disabled={loading}
                    >
                        Back to Tournament Selection
                    </Button>

                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                        {hasGaRequirements && (
                            <Button
                                variant="outlined"
                                onClick={handleSkipGaTickets}
                                disabled={loading}
                                size="large"
                            >
                                Skip & Continue
                            </Button>
                        )}

                        <Button
                            variant="contained"
                            onClick={handleAddGaTickets}
                            disabled={loading}
                            size="large"
                        >
                            {loading
                                ? "Adding GA Tickets..."
                                : hasGaRequirements
                                ? `Add ${totalGaToAdd} GA Ticket${
                                      totalGaToAdd > 1 ? "s" : ""
                                  } (1 per day) & Continue`
                                : "Continue"}
                        </Button>
                    </Box>
                </Box>
            </Box>
        );
    },
);

export default GaTicketPrompt;
